import { useEffect, useRef, useState } from 'react'
import { DOC_TYPES } from '../hooks/useComplianceDocs.js'
import { supabase } from '../lib/supabase.js'

// Add/edit a compliance document. Used from the Settings screen.
//
// Manager fills:
//   - name (required, free text label)
//   - doc_type (required, dropdown)
//   - holder_name (optional — only for per-employee certs)
//   - expiration_date (optional, but the whole point of the feature)
//   - notes (optional)
//
// Two ways in:
//   1. Manual entry — type everything in.
//   2. "Scan to autofill" — snap a photo of the actual document, the
//      extract-document-expiration edge function reads it via Claude
//      Vision and pre-fills doc_type, expiration_date, holder_name,
//      and an explanatory note. Manager confirms and saves.
//
// We don't persist the photo itself — this is an autofill aid, not
// a doc-storage feature. Storage upload is a future enhancement.
export default function ComplianceDocEditModal({
  open,
  doc = null,             // null when adding a new doc
  onClose,
  onSave,                 // (payload) => Promise<void>
  onArchive,              // (id) => Promise<void>  — only for existing docs
}) {
  const [name, setName]                     = useState('')
  const [docType, setDocType]               = useState('food_handler_cert')
  const [holderName, setHolderName]         = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [notes, setNotes]                   = useState('')
  const [submitting, setSubmitting]         = useState(false)
  const [error, setError]                   = useState(null)

  // Photo-extraction state. We surface this inline above the form so
  // it's discoverable without yet another modal-on-modal.
  const [scanning, setScanning]   = useState(false)
  const [scanResult, setScanResult] = useState(null)   // { confidence, gotDate } after a successful scan
  const fileInputRef = useRef(null)

  // Reset form when the modal opens, or hydrate from existing doc.
  useEffect(() => {
    if (!open) return
    if (doc) {
      setName(doc.name || '')
      setDocType(doc.doc_type || 'food_handler_cert')
      setHolderName(doc.holder_name || '')
      setExpirationDate(doc.expiration_date || '')
      setNotes(doc.notes || '')
    } else {
      setName('')
      setDocType('food_handler_cert')
      setHolderName('')
      setExpirationDate('')
      setNotes('')
    }
    setError(null)
    setScanning(false)
    setScanResult(null)
    setSubmitting(false)
  }, [open, doc])

  if (!open) return null

  async function handleSubmit(e) {
    e.preventDefault()
    if (submitting) return
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        doc_type: docType,
        holder_name: holderName.trim() || null,
        expiration_date: expirationDate || null,
        notes: notes.trim() || null,
      }
      await onSave(payload)
      onClose()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ComplianceDocEditModal] save failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
    } finally {
      // Always reset submitting, even on success — modals stay mounted
      // between opens, so leaving submitting=true would lock the form
      // on the next open. (Pattern noted in feedback memory.)
      setSubmitting(false)
    }
  }

  async function handleArchive() {
    if (!doc?.id || !onArchive) return
    if (!window.confirm('Archive this document? It will be hidden from the list but kept for records.')) return
    setSubmitting(true)
    setError(null)
    try {
      await onArchive(doc.id)
      onClose()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ComplianceDocEditModal] archive failed', e)
      setError(`Could not archive: ${e?.message || 'try again.'}`)
    } finally {
      setSubmitting(false)
    }
  }

  // Photo-to-autofill flow. Triggered when the user picks an image
  // from the hidden <input>. We base64-encode it client-side and POST
  // to the extract-document-expiration edge function. The result
  // pre-fills the form; the manager confirms before saving.
  async function handlePhotoSelected(event) {
    const file = event.target.files?.[0]
    // Reset the input so picking the SAME file twice still triggers
    // a change event (browsers de-duplicate by default).
    if (event.target) event.target.value = ''
    if (!file) return

    setScanning(true)
    setError(null)
    setScanResult(null)

    try {
      const base64 = await fileToBase64(file)
      const { data, error: fnError } = await supabase.functions.invoke(
        'extract-document-expiration',
        { body: { image_base64: base64, media_type: file.type || 'image/jpeg' } },
      )

      if (fnError) throw fnError
      if (!data) throw new Error('Empty response from extractor.')
      if (data.error) {
        throw new Error(
          data.error === 'image_unreadable'
            ? "Couldn't read that photo — try a clearer shot in good light."
            : data.message || 'The extractor reported an error.',
        )
      }

      // Pre-fill the form. Manager can edit anything before saving —
      // we never silently overwrite a value the user already typed.
      // On a fresh "Add" form (typical scan flow), all fields are
      // empty so there's nothing to clobber.
      if (data.doc_type)                   setDocType(data.doc_type)
      if (data.expiration_date)            setExpirationDate(data.expiration_date)
      if (data.holder_name && !holderName) setHolderName(data.holder_name)

      // Build a sensible default label if the user hasn't typed one yet.
      if (!name.trim()) {
        const labelGuess = labelForExtraction(data)
        if (labelGuess) setName(labelGuess)
      }

      // Append a note line so the manager can see what the AI thought
      // it read (issuing authority, license number, etc.). Keeps any
      // existing notes the user already typed.
      const aiNote = composeNote(data)
      if (aiNote) {
        setNotes((prev) => (prev ? `${prev}\n${aiNote}` : aiNote))
      }

      setScanResult({
        confidence: data.confidence || 'low',
        gotDate: !!data.expiration_date,
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ComplianceDocEditModal] scan failed', e)
      setError('Could not read the document — please try again in a moment.')
    } finally {
      setScanning(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky error banner */}
        {error && (
          <div className="sticky top-0 z-10 rounded-t-3xl bg-red-100 px-4 py-3 text-sm font-medium text-red-900">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-lg font-medium">
              {doc ? 'Edit document' : 'Add document'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium text-black/55 hover:text-black/85"
            >
              Cancel
            </button>
          </div>

          {/* Scan-to-autofill — only shown when adding a new doc.
              Editing an existing record is usually about correcting a
              date the manager already knows; pulling photo extraction
              into the edit flow would be confusing. */}
          {!doc && (
            <div className="rounded-xl border border-dashed border-blue-300 bg-blue-50/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-blue-900">
                    ✨ Scan to autofill
                  </div>
                  <p className="mt-0.5 text-xs leading-snug text-blue-900/75">
                    Snap a photo of the document — we&rsquo;ll read the expiration date
                    and fill in the form. You can still edit anything.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={scanning}
                  className="shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {scanning ? 'Reading…' : 'Take photo'}
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoSelected}
                className="hidden"
              />

              {/* Confidence/result chip after a scan. */}
              {scanResult && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-blue-900/85">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    scanResult.confidence === 'high'
                      ? 'bg-status-green-bg text-status-green-text'
                      : scanResult.confidence === 'medium'
                        ? 'bg-status-amber-bg text-status-amber-text'
                        : 'bg-status-red-bg text-status-red-text'
                  }`}>
                    {scanResult.confidence} confidence
                  </span>
                  {scanResult.gotDate
                    ? <span>Date filled — please confirm before saving.</span>
                    : <span>No date found — type it in below.</span>}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="cd-type">
              Type
            </label>
            <select
              id="cd-type"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="cd-name">
              Label <span className="text-red-600">*</span>
            </label>
            <input
              id="cd-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Liquor License or Carlos — Food Handler"
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <div className="mt-1 text-xs text-black/55">
              Short label that&rsquo;ll show in the list. Include a person&rsquo;s name for per-employee certs.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="cd-holder">
              Holder name <span className="text-black/45">(optional)</span>
            </label>
            <input
              id="cd-holder"
              type="text"
              value={holderName}
              onChange={(e) => setHolderName(e.target.value)}
              placeholder="Carlos Garcia"
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="cd-exp">
              Expiration date
            </label>
            <input
              id="cd-exp"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
            <div className="mt-1 text-xs text-black/55">
              Leave blank if you don&rsquo;t know it yet — you&rsquo;ll get a heads-up nudge to fill it in.
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70" htmlFor="cd-notes">
              Notes <span className="text-black/45">(optional)</span>
            </label>
            <textarea
              id="cd-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Renewal contact, fee, where the original lives, etc."
              className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting || !name.trim()}
              className="flex-1 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Saving…' : doc ? 'Save changes' : 'Add document'}
            </button>
            {doc && onArchive && (
              <button
                type="button"
                onClick={handleArchive}
                disabled={submitting}
                className="rounded-lg border border-red-300 px-3 py-3 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Archive
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}

// Build a sensible default label from the extraction. The manager can
// always rename — this is just a starting point so the field isn't
// blank after a successful scan.
function labelForExtraction(data) {
  const typeMap = {
    liquor_license:    'Liquor License',
    food_handler_cert: 'Food Handler',
    business_license:  'Business License',
    health_permit:     'Health Permit',
    insurance:         'Insurance',
    workers_comp:      "Workers' Comp",
    sales_tax_permit:  'Sales Tax Permit',
    fire_safety:       'Fire Safety',
    music_license:     'Music License',
    lease:             'Lease',
    other:             'Compliance Document',
  }
  const typeLabel = typeMap[data.doc_type] || 'Compliance Document'

  // For per-employee certs, prefix with the employee name.
  if (data.doc_type === 'food_handler_cert' && data.holder_name) {
    return `${data.holder_name} — Food Handler`
  }
  return typeLabel
}

// Compose a single note line summarizing what we read off the doc.
function composeNote(data) {
  const bits = []
  if (data.issuing_authority) bits.push(`Issued by ${data.issuing_authority}`)
  if (data.notes)              bits.push(data.notes)
  if (!bits.length) return null
  return `📷 ${bits.join(' · ')}`
}

// FileReader wrapper that returns the bare base64 (no data: prefix).
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = String(reader.result || '')
      const stripped = result.replace(/^data:[^;]+;base64,/, '')
      resolve(stripped)
    }
    reader.onerror = () => reject(reader.error || new Error('Could not read file'))
    reader.readAsDataURL(file)
  })
}
